import { useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"

interface GlassDrawerProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  width?: string
}

const springTransition = {
  type: "spring" as const,
  stiffness: 400,
  damping: 30,
}

export function GlassDrawer({
  isOpen,
  onClose,
  title,
  children,
  width = "min(90vw, 450px)",
}: GlassDrawerProps) {
  const handleEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (!isOpen) return
    document.addEventListener("keydown", handleEsc)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", handleEsc)
      document.body.style.overflow = ""
    }
  }, [isOpen, handleEsc])

  if (typeof document === "undefined" || !isOpen) return null

  // #193: SEM AnimatePresence de propósito. O drawer é renderizado apenas
  // enquanto `isOpen` — ao fechar, desmonta NA HORA. Antes, com
  // AnimatePresence, um re-render que coincidisse com a animação de saída
  // (ex.: a captura de tela em segundo plano falhando/terminando, ou o
  // estado de "sucesso" após criar um chamado) interrompia o exit e o
  // overlay `fixed inset-0` ficava preso na tela capturando TODOS os cliques
  // — a página travava e só voltava com refresh. Verificado pelo fiber do
  // React: isOpen=false mas o DOM continuava montado. A animação de ENTRADA
  // (fade do backdrop + slide-in do painel) é mantida via initial/animate;
  // o fechamento é instantâneo (sem slide-out), o que é um trade-off
  // aceitável para nunca mais travar.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "drawer-title" : undefined}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/40 backdrop-blur-[8px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <motion.div
        className={cn(
          "relative h-full z-10 flex flex-col",
          "backdrop-blur-[40px] bg-glass-modal",
          "border-l border-glass-border",
          "shadow-2xl shadow-black/30"
        )}
        style={{ width }}
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        transition={springTransition}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-glass-border shrink-0">
          {title && (
            <h2
              id="drawer-title"
              className="text-lg font-semibold text-[var(--text-primary)]"
            >
              {title}
            </h2>
          )}
          <button
            onClick={onClose}
            aria-label="Fechar painel"
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-lg ml-auto",
              "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]",
              "hover:bg-glass-hover",
              "transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)]"
            )}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-5">{children}</div>
      </motion.div>
    </div>,
    document.body
  )
}
